
import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SubscriptionFormValues } from "./SubscriptionFormSchema";
import { SubscriptionType } from "@/types/subscriptionType";
import { useCurrency } from "@/contexts/CurrencyContext";

interface SubscriptionTypeFieldProps {
  control: Control<SubscriptionFormValues>;
  subscriptionTypes?: SubscriptionType[];
  onTypeSelect?: (type: SubscriptionType | null) => void;
}

export function SubscriptionTypeField({
  control,
  subscriptionTypes = [],
  onTypeSelect,
}: SubscriptionTypeFieldProps) {
  const { t } = useTranslation("subscriptions");
  const { formatCurrency } = useCurrency();

  const handleValueChange = (typeId: string, onChange: (value: string) => void) => {
    const selectedType = subscriptionTypes.find(st => st.id === typeId);
    if (selectedType) {
      onChange(selectedType.name);
      onTypeSelect?.(selectedType);
    }
  };

  // If no dynamic types provided, show fallback
  if (subscriptionTypes.length === 0) {
    return (
      <FormField
        control={control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("type")}</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectType")} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Standard">{t("types.standard")}</SelectItem>
                <SelectItem value="Premium">{t("types.premium")}</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription>
              {t("typeDescription")}
            </FormDescription>
          </FormItem>
        )}
      />
    );
  }

  return (
    <FormField
      control={control}
      name="type"
      render={({ field }) => {
        const currentTypeId = subscriptionTypes.find(st => st.name === field.value)?.id || "";
        
        return (
          <FormItem>
            <FormLabel>{t("type")}</FormLabel>
            <Select
              onValueChange={(value) => handleValueChange(value, field.onChange)}
              value={currentTypeId}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectType")} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {subscriptionTypes.map((st) => (
                  <SelectItem key={st.id} value={st.id}>
                    {st.name} - {st.sessions_total} {t("types.sessions")} - {formatCurrency(st.price)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>
              {t("typeDescription")}
            </FormDescription>
          </FormItem>
        );
      }}
    />
  );
}
